import { memo, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { ClipType } from '@air/api/types';
import {
  setUploadImageLoadingAction,
  setUploadImagePreviewAction,
  setUploadPreviewFailedAction,
  type Upload,
} from '@air/redux-uploader';

import { isPreviewAvailable } from '../utils';
import { exceedsBrowserImagePreviewLimit } from '../utils/exceedsBrowserImagePreviewLimit';
import { loadUploadImagePreview } from '../utils/loadUploadImagePreview';
import { loadUploadVideoPreview } from '../utils/loadUploadVideoPreview';

export type UploadPreviewLoaderProps = {
  upload: Upload;
};

export const UploadPreviewLoader = memo(({ upload }: UploadPreviewLoaderProps) => {
  const dispatch = useDispatch();
  const canGeneratePreview = isPreviewAvailable(upload);
  const ext = upload.apiUploadData.ext;
  const file = upload.file;
  const imageBase64 = upload.imageBase64;
  const previewFailed = upload.previewFailed;
  const type = upload.apiUploadData.type;
  const uploadId = upload.id;

  useEffect(() => {
    if (!canGeneratePreview || imageBase64 || previewFailed) {
      return;
    }

    if (type !== ClipType.video && exceedsBrowserImagePreviewLimit(file.size)) {
      dispatch(setUploadPreviewFailedAction({ uploadId }));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      dispatch(setUploadPreviewFailedAction({ uploadId }));
      return;
    }

    dispatch(setUploadImageLoadingAction({ isImageLoading: true, uploadId }));

    const objectUrl = URL.createObjectURL(file);
    let didComplete = false;
    let isCancelled = false;

    const commitPreview = (nextImageBase64: string) => {
      if (isCancelled) {
        return;
      }

      didComplete = true;
      dispatch(setUploadImagePreviewAction({ imageBase64: nextImageBase64, uploadId }));
      URL.revokeObjectURL(objectUrl);
    };

    const failPreview = () => {
      if (isCancelled) {
        return;
      }

      didComplete = true;
      dispatch(setUploadPreviewFailedAction({ uploadId }));
      URL.revokeObjectURL(objectUrl);
    };

    const stopLoading =
      type === ClipType.video
        ? loadUploadVideoPreview({ canvas, ctx, ext, objectUrl, onError: failPreview, onPreview: commitPreview })
        : loadUploadImagePreview({ canvas, ctx, objectUrl, onError: failPreview, onPreview: commitPreview });

    return () => {
      isCancelled = true;
      stopLoading();

      if (!didComplete) {
        URL.revokeObjectURL(objectUrl);
        dispatch(setUploadImageLoadingAction({ isImageLoading: false, uploadId }));
      }
    };
  }, [canGeneratePreview, dispatch, ext, file, imageBase64, previewFailed, type, uploadId]);

  return null;
});

UploadPreviewLoader.displayName = 'UploadPreviewLoader';
